import React, { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import axios from "axios";

import Hero from "./components/Hero";
import BirthForm from "./components/BirthForm";
import Loading from "./components/Loading";
import PaidEmailPanel from "./components/PaidEmailPanel";
import Reading from "./components/Reading";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [stage, setStage] = useState("intro"); // intro | loading | reading
  const [reading, setReading] = useState(null);
  const navigate = useNavigate();

  const scrollToForm = () => {
    document.getElementById("birth-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const submitForm = async (form) => {
    setStage("loading");
    try {
      const payload = {
        birth_date: form.birth_date,
        birth_time: form.time_unknown ? null : form.birth_time,
        time_unknown: !!form.time_unknown,
        city: form.city,
        country: form.country,
        email: form.email,
        name: form.name,
      };
      const start = await axios.post(`${API}/reading/create`, payload, { timeout: 30000 });
      const jobId = start.data.id;

      const maxAttempts = 60;
      let attempt = 0;
      const poll = async () => {
        attempt += 1;
        try {
          const res = await axios.get(`${API}/reading/${jobId}`);
          const status = res.data.status;
          if (status === "complete") {
            setReading(res.data);
            setStage("reading");
            navigate(`/reading/${jobId}`);
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
          }
          if (status === "failed") {
            toast.error(res.data.error || "The reading could not be completed.");
            setStage("intro");
            return;
          }
          if (attempt >= maxAttempts) {
            toast.error("Timed out waiting for the reading.");
            setStage("intro");
            return;
          }
          setTimeout(poll, 3000);
        } catch (e) {
          if (attempt >= maxAttempts) {
            toast.error("Timed out waiting for the reading.");
            setStage("intro");
            return;
          }
          setTimeout(poll, 3000);
        }
      };
      poll();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail ||
          err?.message ||
          "Something interrupted the calculation. Please try again."
      );
      setStage("intro");
    }
  };

  const reset = () => {
    setReading(null);
    setStage("intro");
    navigate("/");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <PageShell>
      {stage === "intro" && (
        <>
          <Hero onStart={scrollToForm} />
          <BirthForm onSubmit={submitForm} loading={false} />
        </>
      )}
      {stage === "loading" && <Loading />}
      {stage === "reading" && <Reading reading={reading} onReset={reset} />}
    </PageShell>
  );
};

// ----- /reading/:id route — used for direct revisits AND Stripe return -----
const ReadingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reading, setReading] = useState(null);
  const [stage, setStage] = useState("loading"); // loading | expanding | reading | failed
  const [expansionError, setExpansionError] = useState("");
  const sessionIdParam = searchParams.get("session_id");
  const paidParam = searchParams.get("paid");

  // Track polling so we can re-trigger after a retry without effect re-mount.
  const pollRef = React.useRef({ cancelled: false, attempts: 0 });

  const fetchAndAdvance = React.useCallback(
    async function tick() {
      pollRef.current.attempts += 1;
      try {
        const res = await axios.get(`${API}/reading/${id}`);
        const data = res.data;
        if (pollRef.current.cancelled) return;
        if (data.status === "complete") {
          setReading(data);
          const status = data.expansion_status;
          // Any reading that ever entered the paid pipeline (processing /
          // partial / complete with expanded=true / failed-after-paid) lives
          // on the email panel, NOT the on-page reading. The free reading
          // view is only for users who haven't paid yet.
          const onPaidPath =
            status === "processing" ||
            status === "partial" ||
            status === "complete" ||
            status === "failed" ||
            data.expanded;
          if (onPaidPath) {
            setStage("paid_email");
            // Keep polling while the weaving is still running OR while we are
            // waiting for the email to be sent — that lets the panel update
            // its badge from "Preparing" → "Delivered" automatically.
            const stillWorking =
              status === "processing" ||
              (data.expanded && data.email_status === "pending");
            if (stillWorking && pollRef.current.attempts < 260) {
              setTimeout(fetchAndAdvance, 3000);
            }
            return;
          }
          // Free path
          setStage("reading");
          return;
        }
        if (data.status === "failed") {
          toast.error(data.error || "Reading failed");
          return;
        }
        if (pollRef.current.attempts < 120) setTimeout(fetchAndAdvance, 3000);
      } catch (e) {
        if (pollRef.current.attempts < 30) setTimeout(fetchAndAdvance, 3000);
        else toast.error("Reading not found.");
      }
    },
    [id],
  );

  React.useEffect(() => {
    pollRef.current = { cancelled: false, attempts: 0 };

    const verifyAndStart = async () => {
      // If we returned from Stripe with a session_id, poll the checkout status
      // up to 6 times to confirm payment before fetching the (now expanded)
      // reading.
      if (sessionIdParam) {
        toast.info("Verifying payment…");
        let pollCount = 0;
        const maxPolls = 6;
        const pollPayment = async () => {
          pollCount += 1;
          try {
            const res = await axios.get(`${API}/checkout/status/${sessionIdParam}`);
            if (res.data.payment_status === "paid") {
              toast.success("Payment confirmed — your full Codex is unlocking.");
              setSearchParams({});
              fetchAndAdvance();
              return;
            }
            if (res.data.status === "expired") {
              toast.error("Payment session expired. Please try again.");
              setSearchParams({});
              fetchAndAdvance();
              return;
            }
            if (pollCount < maxPolls) setTimeout(pollPayment, 2500);
            else {
              toast.message("Payment is still processing — check back soon.");
              setSearchParams({});
              fetchAndAdvance();
            }
          } catch (e) {
            if (pollCount < maxPolls) setTimeout(pollPayment, 2500);
            else fetchAndAdvance();
          }
        };
        pollPayment();
        return;
      }
      if (paidParam === "cancelled") {
        toast.message("Checkout was cancelled — your entry-level reading is still here.");
        setSearchParams({});
      }
      fetchAndAdvance();
    };

    verifyAndStart();
    return () => {
      pollRef.current.cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const startCheckout = async (couponCode) => {
    try {
      const payload = {
        reading_id: id,
        origin_url: window.location.origin,
      };
      if (couponCode) payload.coupon_code = couponCode;
      const res = await axios.post(`${API}/checkout/session`, payload);
      if (res.data.coupon_applied) {
        toast.success("Coupon applied — your Codex is being woven.");
      }
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
    }
  };

  const retryExpansion = async () => {
    try {
      await axios.post(`${API}/reading/${id}/retry-expansion`);
      toast.success("Re-weaving your Codex — hold on.");
      setExpansionError("");
      setStage("paid_email");
      // restart polling cycle
      pollRef.current = { cancelled: false, attempts: 0 };
      fetchAndAdvance();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not restart Codex weaving.");
    }
  };

  const resendEmail = async () => {
    try {
      const res = await axios.post(`${API}/reading/${id}/resend-email`);
      toast.success(`Email sent to ${res.data.to}.`);
      pollRef.current = { cancelled: false, attempts: 0 };
      fetchAndAdvance();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
          "Could not send the email — try again in a minute.",
      );
    }
  };

  return (
    <PageShell>
      {stage === "loading" && <Loading />}
      {stage === "paid_email" && (
        <PaidEmailPanel
          reading={reading}
          expansionStatus={reading?.expansion_status}
          emailStatus={reading?.email_status}
          onResendEmail={resendEmail}
          onRetryExpansion={retryExpansion}
        />
      )}
      {stage === "reading" && (
        <Reading
          reading={reading}
          onReset={() => navigate("/")}
          onUnlock={startCheckout}
        />
      )}
    </PageShell>
  );
};

const PageShell = ({ children }) => (
  <div className="App" data-testid="app-root">
    {children}
    <footer
      className="border-t border-[var(--sf-border)] py-12 text-center print:hidden"
      data-testid="site-footer"
    >
      <div className="overline mb-3">Soul Femme Codex</div>
      <p className="text-xs tracking-widest uppercase text-[var(--sf-text-muted)] mb-4">
        A modern mystic's mirror
      </p>
      <a
        href="https://andreakorin.com/"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block font-display italic text-base text-[var(--sf-leopard-gold)] hover:opacity-80 transition-opacity"
        data-testid="footer-andreakorin-link"
      >
        andreakorin.com
      </a>
    </footer>
    <Toaster
      theme="dark"
      position="top-center"
      richColors
      duration={8000}
      toastOptions={{
        style: {
          background: "#1E1A20",
          border: "1px solid rgba(212, 175, 55, 0.3)",
          color: "#FDFBF7",
        },
      }}
    />
  </div>
);

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/reading/:id" element={<ReadingPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
